import { getBalanceByLevels } from "@/api/financialReport";
import { consoleHandlerError } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// Estado del Resumen de Balance. A diferencia de assetsReport (que trae un solo
// nivel-1 con acumulados/ratio/YoY), acá guardamos los tres grupos de nivel 1
// (Activos/Pasivos/Patrimonio) con su jerarquía L2/L3 y sólo la evolución de
// montos por período — la tabla replica el Resumen de Estado de Resultados.
//
// `groups`      : [{ level1, level2Accounts, hierarchy }] en orden contable
//                 (Activos → Pasivos → Patrimonio), tal como los devuelve el
//                 backend. Los montos NO viven acá: se leen desde `accountsData`.
// `accountsData`: { label: { header: amount } } — plano y compartido entre
//                 grupos (los nombres de cuenta L2/L3 no colisionan entre
//                 grupos). El tab lo indexa por label + header para armar filas.
// `monthHeaders`: cabeceras del período (mismo buildHeadersByPeriod que el resto
//                 del reporte), en orden oldest→newest.
const state = () => ({
    groups: [],
    accountsData: {},
    monthHeaders: [],
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
});

const mutations = {
    SET_BALANCE_DATA(state, { groups, accountsData, monthHeaders, periodRange }) {
        state.groups = groups;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
    },
    SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
    SET_ERROR(state, error) { state.error = error; },
};

const actions = {
    async fetchBalanceData({ commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
                rootState.reportsFilters.customYearRange,
            );

            const response = await getBalanceByLevels(initDate, endDate, filterPeriod);
            const rawGroups = response.data.groups || [];

            const monthHeaders = buildHeadersByPeriod(initDate, endDate, filterPeriod);

            // Aplanamos report_data de cada grupo a { label: { header: amount } }.
            // Un mismo label sólo pertenece a un grupo, así que el merge global es
            // seguro (no hay colisiones entre Activos/Pasivos/Patrimonio).
            const accountsData = {};
            const groups = rawGroups.map((group) => {
                Object.keys(group.report_data || {}).forEach((label) => {
                    accountsData[label] = accountsData[label] || {};
                    group.report_data[label].forEach((row) => {
                        const header = formatPeriodLabel(row.period, filterPeriod);
                        const amount = Number(row.total_amount);
                        accountsData[label][header] = isNaN(amount) ? 0 : amount;
                    });
                });
                return {
                    level1: group.level_1,
                    level2Accounts: group.level2_accounts || [],
                    hierarchy: group.hierarchy || {},
                };
            });

            commit("SET_BALANCE_DATA", {
                groups,
                accountsData,
                monthHeaders,
                periodRange: { init_date: initDate, end_date: endDate },
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[balanceReport]", silent: true });
            commit("SET_ERROR", "Error al cargar el balance.");
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
